import { useId } from 'react';

import type { SiteCmsMediaQuota } from '@shared/types/entities';

import { cmsBytes, cmsQuotaStatus } from './site-cms-storage-model';

type Props = {
  kind: 'images' | 'documents';
  quota?: SiteCmsMediaQuota;
  loading: boolean;
  failed: boolean;
  refreshing?: boolean;
  retry: () => void;
};
export default function SiteCmsStorageQuota({ kind, quota, loading, failed, refreshing, retry }: Props) {
  const id = useId();
  if (failed)
    return (
      <div className='cms-quota-error' role='alert'>
        Le quota ne peut pas être vérifié. Les fichiers existants restent consultables, mais l’import est suspendu.{' '}
        <button className='cms-link-button' type='button' onClick={retry}>
          Réessayer les quotas
        </button>
      </div>
    );
  if (loading || !quota)
    return (
      <div className='cms-storage-quota' role='status'>
        Vérification de l’espace disponible…
      </div>
    );
  const status = cmsQuotaStatus(quota);
  const remaining = Math.max(0, quota.maxBytes - quota.usedBytes);
  const availableFiles = Math.max(0, quota.maxCount - quota.count);
  const label = kind === 'images' ? 'Images & logos' : 'Documents PDF';
  const percent = Math.min(100, Math.max(quota.usedBytes / quota.maxBytes, quota.count / quota.maxCount) * 100);
  return (
    <section className={`cms-storage-quota is-${status}`} aria-labelledby={id}>
      <div className='cms-quota-header'>
        <strong id={id}>{label} · stockage privé</strong>
        <span>
          {status === 'full'
            ? 'Quota atteint'
            : status === 'warning'
              ? 'Capacité bientôt atteinte'
              : 'Espace disponible'}
        </span>
      </div>
      <div className='cms-quota-metrics'>
        <div>
          <b>{cmsBytes(quota.usedBytes)}</b>
          <span>sur {cmsBytes(quota.maxBytes)}</span>
        </div>
        <div>
          <b>
            {quota.count} / {quota.maxCount}
          </b>
          <span>{kind === 'images' ? 'images' : 'documents'}</span>
        </div>
      </div>
      <progress className='cms-quota-progress' max={100} value={percent} aria-label={`Capacité utilisée : ${label}`} />
      <p className='cms-quota-remaining'>
        <strong>{cmsBytes(remaining)}</strong> et{' '}
        <strong>
          {availableFiles} emplacement{availableFiles > 1 ? 's' : ''}
        </strong>{' '}
        restants.
      </p>
      <p className='cms-hint'>
        {cmsBytes(quota.maxUploadBytes)} maximum par fichier.{' '}
        {kind === 'images'
          ? 'Le stockage est calculé après optimisation WebP, pas sur le fichier original.'
          : 'Le PDF importé compte pour sa taille réelle.'}{' '}
        {refreshing ? 'Actualisation…' : ''}
      </p>
      {status !== 'available' && (
        <p className='cms-quota-guidance'>
          {status === 'full' ? 'Vous pouvez réutiliser vos fichiers existants. ' : ''}Retirez les fichiers inutilisés
          pour libérer de la place. Ceux retenus par une version ne sont pas supprimables ici.
        </p>
      )}
      <small>
        Quota de la bibliothèque de cette structure ; les copies des versions publiées ne sont pas incluses dans ce
        compteur.
      </small>
    </section>
  );
}
